/**
 * Priority Queue for Poker Reborn Effects Engine
 *
 * Generic priority queue implementation for managing effects by priority.
 *
 * @see tasks/active/EFFECTS_ENGINE_IMPLEMENTATION_PLAN.md - Phase 2.3
 */

// =============================================================================
// PRIORITY QUEUE ITEM
// =============================================================================

/**
 * Item in the priority queue with its priority value.
 */
export interface PriorityQueueItem<T> {
  /** The item */
  item: T;
  /** Priority value (higher = more important) */
  priority: number;
  /** Insertion order for stable sorting */
  insertionOrder: number;
}

// =============================================================================
// PRIORITY QUEUE CLASS (P2.3.1-P2.3.8)
// =============================================================================

/**
 * Generic priority queue that orders items by priority (highest first).
 *
 * Features:
 * - O(n) enqueue (maintains sorted order)
 * - O(1) dequeue and peek
 * - Stable sorting (same priority items maintain insertion order)
 * - Remove by predicate for cancellation
 */
export class PriorityQueue<T> {
  private _items: PriorityQueueItem<T>[] = [];
  private _insertionCounter = 0;

  // ---------------------------------------------------------------------------
  // Properties
  // ---------------------------------------------------------------------------

  /**
   * Number of items in the queue.
   */
  get size(): number {
    return this._items.length;
  }

  /**
   * Whether the queue is empty.
   */
  get isEmpty(): boolean {
    return this._items.length === 0;
  }

  // ---------------------------------------------------------------------------
  // Core Operations (P2.3.2-P2.3.4)
  // ---------------------------------------------------------------------------

  /**
   * Add an item to the queue with the given priority.
   * Higher priority items are dequeued first.
   *
   * @param item The item to add
   * @param priority Priority value (higher = more important)
   */
  enqueue(item: T, priority: number): void {
    const queueItem: PriorityQueueItem<T> = {
      item,
      priority,
      insertionOrder: this._insertionCounter++,
    };

    // Find insertion point (binary search for efficiency)
    let low = 0;
    let high = this._items.length;

    while (low < high) {
      const mid = Math.floor((low + high) / 2);
      const midItem = this._items[mid];

      if (!midItem) {
        break;
      }

      // Higher priority should come first
      // Same priority: earlier insertion comes first (stable)
      if (
        midItem.priority > priority ||
        (midItem.priority === priority &&
          midItem.insertionOrder < queueItem.insertionOrder)
      ) {
        low = mid + 1;
      } else {
        high = mid;
      }
    }

    this._items.splice(low, 0, queueItem);
  }

  /**
   * Remove and return the highest priority item.
   * Returns undefined if queue is empty.
   */
  dequeue(): T | undefined {
    const item = this._items.shift();
    return item?.item;
  }

  /**
   * View the highest priority item without removing it.
   * Returns undefined if queue is empty.
   */
  peek(): T | undefined {
    const item = this._items[0];
    return item?.item;
  }

  /**
   * Get the priority of the highest priority item.
   * Returns undefined if queue is empty.
   */
  peekPriority(): number | undefined {
    const item = this._items[0];
    return item?.priority;
  }

  // ---------------------------------------------------------------------------
  // Removal Operations (P2.3.5-P2.3.6)
  // ---------------------------------------------------------------------------

  /**
   * Remove items matching a predicate.
   * Returns the number of items removed.
   *
   * @param predicate Function that returns true for items to remove
   */
  remove(predicate: (item: T) => boolean): number {
    const originalLength = this._items.length;
    this._items = this._items.filter((qi) => !predicate(qi.item));
    return originalLength - this._items.length;
  }

  /**
   * Remove a specific item from the queue.
   * Returns true if the item was found and removed.
   *
   * @param item The item to remove
   */
  removeItem(item: T): boolean {
    const index = this._items.findIndex((qi) => qi.item === item);
    if (index >= 0) {
      this._items.splice(index, 1);
      return true;
    }
    return false;
  }

  /**
   * Remove and return all items matching a predicate.
   *
   * @param predicate Function that returns true for items to remove
   */
  extractMatching(predicate: (item: T) => boolean): T[] {
    const matching: T[] = [];
    this._items = this._items.filter((qi) => {
      if (predicate(qi.item)) {
        matching.push(qi.item);
        return false;
      }
      return true;
    });
    return matching;
  }

  /**
   * Clear all items from the queue.
   */
  clear(): void {
    this._items = [];
    this._insertionCounter = 0;
  }

  // ---------------------------------------------------------------------------
  // Query Operations
  // ---------------------------------------------------------------------------

  /**
   * Check if any item matches a predicate.
   *
   * @param predicate Function to test items
   */
  has(predicate: (item: T) => boolean): boolean {
    return this._items.some((qi) => predicate(qi.item));
  }

  /**
   * Find an item matching a predicate (without removing).
   *
   * @param predicate Function to test items
   */
  find(predicate: (item: T) => boolean): T | undefined {
    const found = this._items.find((qi) => predicate(qi.item));
    return found?.item;
  }

  /**
   * Get all items (in priority order) without removing them.
   */
  toArray(): T[] {
    return this._items.map((qi) => qi.item);
  }

  /**
   * Iterate over items in priority order.
   */
  forEach(callback: (item: T, priority: number) => void): void {
    for (const qi of this._items) {
      callback(qi.item, qi.priority);
    }
  }

  /**
   * Get items grouped by priority.
   */
  groupByPriority(): Map<number, T[]> {
    const groups = new Map<number, T[]>();
    for (const qi of this._items) {
      const group = groups.get(qi.priority);
      if (group) {
        group.push(qi.item);
      } else {
        groups.set(qi.priority, [qi.item]);
      }
    }
    return groups;
  }
}
